import { z } from 'zod';
import {
  ExecutionLineItemApiListResponse,
  ExecutionLineItemApiResponse,
  ExecutionLineItemApiUpdateRequest,
  LineItemApiListResponse,
  LineItemApiResponse,
  ProposalLineItemApiUpdateRequest
} from 'shared/src/api/line-item-api-types';
import {
  MediaPlanApiCreateRequest,
  MediaPlanApiResponse,
  MediaPlanApiUpdateRequest
} from 'shared/src/api/media-plan-api-types';
import {
  CampaignMetrics,
  LineItemMetricV2,
  MediaPlanMetrics
} from 'shared/src/domain/metrics/metrics-types';

import { CreateProposalIOApiResponse } from 'shared/src/api/io-api-types';

/*****************************************************************************
 * Base Request Schemas
 *
 * Foundation schemas for strategy line item and media plan identification used across
 * all tRPC endpoints.
 *****************************************************************************/

export const TrpcStrategyIdentifier = z.object({
  strategy_id: z.uuid()
});
export type TrpcStrategyIdentifier = z.infer<typeof TrpcStrategyIdentifier>;

export const TrpcLineItemIdentifier = TrpcStrategyIdentifier.extend({
  line_item_id: z.uuid()
});
export type TrpcLineItemIdentifier = z.infer<typeof TrpcLineItemIdentifier>;

export const TrpcMediaPlanIdentifier = TrpcLineItemIdentifier.extend({
  media_plan_id: z.uuid()
});
export type TrpcMediaPlanIdentifier = z.infer<typeof TrpcMediaPlanIdentifier>;

export const TrpcCreateProposalIORequest = z.object({
  proposal_id: z.uuid(),
  zoho_io_id: z.string()
});
export type TrpcCreateProposalIORequest = z.infer<typeof TrpcCreateProposalIORequest>;

export const TrpcCreateProposalIOResponse = CreateProposalIOApiResponse;
export type TrpcCreateProposalIOResponse = z.infer<typeof TrpcCreateProposalIOResponse>;

/*****************************************************************************
 * Line Item Response
 *****************************************************************************/

// With ISODateString, TRPC response uses the same type as API response
export const LineItemTrpcResponse = LineItemApiResponse;
export type LineItemTrpcResponse = z.infer<typeof LineItemTrpcResponse>;

export const LineItemWithMetricsTrpcResponse = LineItemMetricV2.partial().extend(
  LineItemTrpcResponse.shape
);
export type LineItemWithMetricsTrpcResponse = z.infer<typeof LineItemWithMetricsTrpcResponse>;

export const ExecutionLineItemTrpcResponse = ExecutionLineItemApiResponse;
export type ExecutionLineItemTrpcResponse = z.infer<typeof ExecutionLineItemTrpcResponse>;

export const ExecutionLineItemWithMetricsTrpcResponse = LineItemMetricV2.partial().extend(
  ExecutionLineItemTrpcResponse.shape
);
export type ExecutionLineItemWithMetricsTrpcResponse = z.infer<
  typeof ExecutionLineItemWithMetricsTrpcResponse
>;

/*****************************************************************************
 * Line Item Mutation Requests
 *
 * Schemas for creating, updating, deleting, and duplicating line items.
 *****************************************************************************/

export const TrpcUpdateProposalLineItemRequest = TrpcLineItemIdentifier.extend({
  lineItem: ProposalLineItemApiUpdateRequest
});
export type TrpcUpdateProposalLineItemRequest = z.infer<typeof TrpcUpdateProposalLineItemRequest>;

export const TrpcUpdateExecutionLineItemRequest = TrpcLineItemIdentifier.extend({
  lineItem: ExecutionLineItemApiUpdateRequest
});
export type TrpcUpdateExecutionLineItemRequest = z.infer<typeof TrpcUpdateExecutionLineItemRequest>;

/*****************************************************************************
 * Media Plan Requests
 *
 * Schemas for creating and listing media plans within line items.
 *****************************************************************************/

export const TrpcCreateMediaPlanRequest = TrpcLineItemIdentifier.extend({
  media_plan: MediaPlanApiCreateRequest
});
export type TrpcCreateMediaPlanRequest = z.infer<typeof TrpcCreateMediaPlanRequest>;

export const TrpcUpdateMediaPlanRequest = TrpcMediaPlanIdentifier.extend({
  media_plan: MediaPlanApiUpdateRequest
});
export type TrpcUpdateMediaPlanRequest = z.infer<typeof TrpcUpdateMediaPlanRequest>;

export const MediaPlanTrpcResponse = MediaPlanApiResponse;
// TODO [ac] this type should handle conversion from strings to numeric values
export type MediaPlanTrpcResponse = z.infer<typeof MediaPlanTrpcResponse>;

/*****************************************************************************
 * Aggregate Metrics
 *
 * Schemas for campaign-level and line item-level aggregate metrics.
 *****************************************************************************/

export const TrpcCampaignMetrics = CampaignMetrics;
export type TrpcCampaignMetrics = z.infer<typeof TrpcCampaignMetrics>;

export const TrpcLineItemMetrics = LineItemMetricV2;
export type TrpcLineItemMetrics = z.infer<typeof TrpcLineItemMetrics>;

export const TrpcLineItemsMetrics = z.object({
  data: z.array(TrpcLineItemMetrics)
});
export type TrpcLineItemsMetrics = z.infer<typeof TrpcLineItemsMetrics>;

export const TrpcMediaPlanMetrics = MediaPlanMetrics;
export type TrpcMediaPlanMetrics = z.infer<typeof TrpcMediaPlanMetrics>;

export const TrpcMediaPlansMetrics = z.object({
  data: z.array(TrpcMediaPlanMetrics)
});
export type TrpcMediaPlansMetrics = z.infer<typeof TrpcMediaPlansMetrics>;

/*****************************************************************************
 * Line Item Graph Metrics (Combined Actual + Planned Timeseries)
 *
 * Single endpoint combining actual per-media-plan timeseries with
 * planned line-item timeseries, all sourced from CubeJS.
 *****************************************************************************/

export const GranularitySchema = z.enum(['day', 'week', 'month']).default('day');
export type Granularity = z.infer<typeof GranularitySchema>;

export const TrpcGraphDataPoint = z.object({
  bucket: z.string(),
  spend: z.coerce.number(),
  cumulative_spend: z.coerce.number(),
  units: z.coerce.number(),
  cumulative_units: z.coerce.number()
});
export type TrpcGraphDataPoint = z.infer<typeof TrpcGraphDataPoint>;

export const TrpcMediaPlanGraphSeries = z.object({
  media_plan_id: z.string(),
  name: z.string(),
  media_buy_id: z.string(),
  data: z.array(TrpcGraphDataPoint)
});
export type TrpcMediaPlanGraphSeries = z.infer<typeof TrpcMediaPlanGraphSeries>;

export const TrpcLineItemGraphMetrics = z.object({
  media_plans: z.array(TrpcMediaPlanGraphSeries),
  planned: z.array(TrpcGraphDataPoint)
});
export type TrpcLineItemGraphMetrics = z.infer<typeof TrpcLineItemGraphMetrics>;

export const TrpcLineItemGraphMetricsRequest = TrpcLineItemIdentifier.extend({
  granularity: GranularitySchema,
  start_date: z.string(),
  end_date: z.string()
});
export type TrpcLineItemGraphMetricsRequest = z.infer<typeof TrpcLineItemGraphMetricsRequest>;

// With ISODateString, TRPC list response uses the same type as API list response
export const TrpcLineItemListResponse = LineItemApiListResponse;
export type TrpcLineItemListResponse = z.infer<typeof TrpcLineItemListResponse>;

export const TrpcExecutionLineItemListResponse = ExecutionLineItemApiListResponse;
export type TrpcExecutionLineItemListResponse = z.infer<typeof TrpcExecutionLineItemListResponse>;
